/**
 * GAMIFICATION SERVICE
 *
 * This service handles all gamification-related operations:
 * - User progress tracking (XP, levels, streaks)
 * - Achievement unlocking and tracking
 * - Leaderboard updates
 * - Point calculations
 */

import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  Timestamp,
  increment,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { firebaseApp } from '../firebase.js';
import {
  ACHIEVEMENTS,
  POINTS,
  LEVELS,
  getLevelByXP,
  getXPToNextLevel,
  calculateStreakPoints,
  calculateTestPoints,
} from '../JobSeeker/constants/gamification.js';

const db = getFirestore(firebaseApp);

// ============ USER PROGRESS MANAGEMENT ============

/**
 * Initialize user progress when they first sign up
 */
export async function initializeUserProgress(userId) {
  const userProgressRef = doc(db, 'userProgress', userId);

  // Check if already exists
  const existingDoc = await getDoc(userProgressRef);
  if (existingDoc.exists()) {
    return existingDoc.data();
  }

  const initialProgress = {
    userId,
    currentLevel: 1,
    currentLevelName: 'Explorer',
    totalXP: 0,
    xpToNextLevel: 100,
    currentStreak: 0,
    longestStreak: 0,
    lastActivityDate: new Date(),
    streakFreezeTokens: 2,
    totalPoints: 0,
    testsCompleted: 0,
    achievements: [],
    badges: [],
    createdAt: new Date(),
    updatedAt: new Date()
  };

  await setDoc(userProgressRef, {
    ...initialProgress,
    lastActivityDate: Timestamp.fromDate(initialProgress.lastActivityDate),
    createdAt: Timestamp.fromDate(initialProgress.createdAt),
    updatedAt: Timestamp.fromDate(initialProgress.updatedAt)
  });

  return initialProgress;
}

// Persist basic identity on userProgress to improve leaderboard naming
async function ensureUserIdentity(userId, userInfo = {}) {
  const { displayName, name, profileName, email } = userInfo || {};
  const resolvedName = displayName || profileName || name;
  if (!resolvedName && !email) return; // Nothing to persist

  const userProgressRef = doc(db, 'userProgress', userId);
  await setDoc(
    userProgressRef,
    {
      displayName: resolvedName || undefined,
      profileName: resolvedName || undefined,
      name: resolvedName || undefined,
      email: email || undefined,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
}

/**
 * Get user's current progress
 */
export async function getUserProgress(userId) {
  try {
    const userProgressRef = doc(db, 'userProgress', userId);
    const progressDoc = await getDoc(userProgressRef);

    if (!progressDoc.exists()) {
      // Initialize if doesn't exist
      return await initializeUserProgress(userId);
    }

    const data = progressDoc.data();
    return {
      ...data,
      lastActivityDate: data.lastActivityDate?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    };
  } catch (error) {
    console.error('Error getting user progress:', error);
    return null;
  }
}

/**
 * Add XP to user and check for level up
 */
export async function addXP(userId, xpAmount) {
  const progressRef = doc(db, 'userProgress', userId);
  const progress = await getUserProgress(userId);

  if (!progress) {
    await initializeUserProgress(userId);
    return { newXP: xpAmount, leveledUp: false };
  }

  const newXP = progress.totalXP + xpAmount;
  const currentLevel = getLevelByXP(progress.totalXP);
  const newLevel = getLevelByXP(newXP);
  const leveledUp = newLevel.level > currentLevel.level;

  await updateDoc(progressRef, {
    totalXP: newXP,
    currentLevel: newLevel.level,
    currentLevelName: newLevel.name,
    xpToNextLevel: getXPToNextLevel(newXP),
    updatedAt: serverTimestamp()
  });

  return {
    newXP,
    leveledUp,
    newLevel: leveledUp ? newLevel.level : undefined,
    newLevelName: leveledUp ? newLevel.name : undefined
  };
}

/**
 * Add points to user's total (for leaderboard)
 */
export async function addPoints(userId, points) {
  const progressRef = doc(db, 'userProgress', userId);

  await updateDoc(progressRef, {
    totalPoints: increment(points),
    updatedAt: serverTimestamp()
  });

  const updated = await getUserProgress(userId);
  return updated?.totalPoints || 0;
}

// ============ STREAK MANAGEMENT ============

/**
 * Update user's activity streak
 * Call this whenever user performs any activity (login, test, etc.)
 */
export async function updateStreak(userId, userInfo = {}) {
  const progress = await getUserProgress(userId);
  if (!progress) {
    await initializeUserProgress(userId);
    return { currentStreak: 1, streakIncreased: true, milestoneReached: false };
  }

  // Persist identity if provided to keep leaderboard names fresh
  await ensureUserIdentity(userId, userInfo);

  const now = new Date();
  const lastActivity = progress.lastActivityDate;
  const daysSinceLastActivity = Math.floor(
    (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24)
  );

  let newStreak = progress.currentStreak;
  let streakIncreased = false;
  let milestoneReached = false;
  let milestonePoints = 0;

  // Same day activity - no change
  if (daysSinceLastActivity === 0) {
    return {
      currentStreak: newStreak,
      streakIncreased: false,
      milestoneReached: false
    };
  }

  // Next day - increment streak
  if (daysSinceLastActivity === 1) {
    newStreak += 1;
    streakIncreased = true;

    // Check for milestone achievements
    if ([7, 14, 30, 60, 100, 365].includes(newStreak)) {
      milestoneReached = true;
      milestonePoints = POINTS[`STREAK_${newStreak}_DAYS`] || 0;
      await addPoints(userId, milestonePoints);
    }
  }
  // Streak broken (more than 1 day)
  else if (daysSinceLastActivity > 1) {
    // Check if user has freeze tokens
    if (progress.streakFreezeTokens > 0) {
      // Use freeze token - keep streak
      await updateDoc(doc(db, 'userProgress', userId), {
        streakFreezeTokens: increment(-1),
        lastActivityDate: Timestamp.fromDate(now),
        updatedAt: serverTimestamp()
      });
      return {
        currentStreak: newStreak,
        streakIncreased: false,
        milestoneReached: false
      };
    } else {
      // Reset streak
      newStreak = 1;
    }
  }

  // Update database
  const progressRef = doc(db, 'userProgress', userId);
  await updateDoc(progressRef, {
    currentStreak: newStreak,
    longestStreak: Math.max(progress.longestStreak, newStreak),
    lastActivityDate: Timestamp.fromDate(now),
    updatedAt: serverTimestamp()
  });

  // Add daily streak points
  if (streakIncreased) {
    await addPoints(userId, POINTS.DAILY_STREAK);
    await addXP(userId, POINTS.DAILY_STREAK);
  }

  return {
    currentStreak: newStreak,
    streakIncreased,
    milestoneReached,
    milestonePoints
  };
}

/**
 * Reset streak (for testing or manual intervention)
 */
export async function resetStreak(userId) {
  const progressRef = doc(db, 'userProgress', userId);
  await updateDoc(progressRef, {
    currentStreak: 0,
    lastActivityDate: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

// ============ ACHIEVEMENT MANAGEMENT ============

/**
 * Check if user has unlocked a specific achievement
 */
export async function hasAchievement(userId, achievementId) {
  const q = query(
    collection(db, 'achievements'),
    where('userId', '==', userId),
    where('achievementId', '==', achievementId),
    limit(1)
  );

  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Unlock an achievement for a user
 */
export async function unlockAchievement(
  userId,
  achievementId,
  metadata
) {
  // Check if already unlocked
  const alreadyUnlocked = await hasAchievement(userId, achievementId);
  if (alreadyUnlocked) {
    return { unlocked: false };
  }

  const achievement = ACHIEVEMENTS.find(a => a.id === achievementId);
  if (!achievement) {
    console.error(`Achievement ${achievementId} not found`);
    return { unlocked: false };
  }

  // Create achievement record
  const achievementRef = doc(collection(db, 'achievements'));
  const achievementData = {
    id: achievementRef.id,
    userId,
    achievementId: achievement.id,
    achievementType: achievement.category,
    unlockedAt: new Date(),
    points: achievement.points,
    metadata
  };

  await setDoc(achievementRef, {
    ...achievementData,
    unlockedAt: Timestamp.fromDate(achievementData.unlockedAt)
  });

  // Update user progress
  const progressRef = doc(db, 'userProgress', userId);
  const currentProgress = await getUserProgress(userId);
  await updateDoc(progressRef, {
    achievements: [...(currentProgress?.achievements || []), achievement.id],
    updatedAt: serverTimestamp()
  });

  // Award points and XP
  await addPoints(userId, achievement.points);
  await addXP(userId, achievement.points);

  return { unlocked: true, achievement };
}

/**
 * Get all achievements for a user
 */
export async function getUserAchievements(userId) {
  const q = query(
    collection(db, 'achievements'),
    where('userId', '==', userId),
    orderBy('unlockedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      ...data,
      unlockedAt: data.unlockedAt?.toDate() || new Date()
    };
  });
}

/**
 * Check and unlock achievements after test completion
 */
export async function checkTestAchievements(
  userId,
  testScore,
  testTime
) {
  const progress = await getUserProgress(userId);
  if (!progress) return [];

  const unlockedAchievements = [];
  const newTestCount = progress.testsCompleted + 1;

  // Update test count
  await updateDoc(doc(db, 'userProgress', userId), {
    testsCompleted: newTestCount,
    updatedAt: serverTimestamp()
  });

  // Check "First Steps" (first test)
  if (newTestCount === 1) {
    const result = await unlockAchievement(userId, 'first_steps', { testScore });
    if (result.unlocked && result.achievement) {
      unlockedAchievements.push(result.achievement);
    }
  }

  // Check "Test Veteran" (10 tests)
  if (newTestCount === 10) {
    const result = await unlockAchievement(userId, 'test_veteran');
    if (result.unlocked && result.achievement) {
      unlockedAchievements.push(result.achievement);
    }
  }

  // Check "Century Club" (100 tests)
  if (newTestCount === 100) {
    const result = await unlockAchievement(userId, 'century_club');
    if (result.unlocked && result.achievement) {
      unlockedAchievements.push(result.achievement);
    }
  }

  // Check "Perfect Score" (100%)
  if (testScore === 100) {
    const result = await unlockAchievement(userId, 'perfect_score', { testScore });
    if (result.unlocked && result.achievement) {
      unlockedAchievements.push(result.achievement);
    }
  }

  // Check time-based achievements
  const now = new Date();
  const hour = now.getHours();

  // Night Owl (12 AM - 6 AM)
  if (hour >= 0 && hour < 6) {
    const result = await unlockAchievement(userId, 'night_owl', { hour, testScore });
    if (result.unlocked && result.achievement) {
      unlockedAchievements.push(result.achievement);
    }
  }

  // Award test completion points
  const testPoints = calculateTestPoints(testScore, newTestCount);
  await addPoints(userId, testPoints);
  await addXP(userId, testPoints);

  return unlockedAchievements;
}

/**
 * Check performance-based achievements
 */
export async function checkPerformanceAchievements(userId) {
  const unlockedAchievements = [];

  // Get all test results for user
  const resultsQuery = query(
    collection(db, 'jobseekerResults'),
    where('jobSeekerId', '==', userId),
    orderBy('completedAt', 'desc')
  );

  const resultsSnapshot = await getDocs(resultsQuery);
  const results = resultsSnapshot.docs.map(doc => doc.data());

  // Count high scores
  const scores60Plus = results.filter(r => (r.totalScore || r.score || 0) >= 60).length;
  const scores75Plus = results.filter(r => (r.totalScore || r.score || 0) >= 75).length;
  const scores90Plus = results.filter(r => (r.totalScore || r.score || 0) >= 90).length;
  const scores95Plus = results.filter(r => (r.totalScore || r.score || 0) >= 95).length;

  // Check Bronze Standard (60%+ on 5 tests)
  if (scores60Plus >= 5) {
    const result = await unlockAchievement(userId, 'bronze_standard', { count: scores60Plus });
    if (result.unlocked && result.achievement) {
      unlockedAchievements.push(result.achievement);
    }
  }

  // Check Silver Standard (75%+ on 10 tests)
  if (scores75Plus >= 10) {
    const result = await unlockAchievement(userId, 'silver_standard', { count: scores75Plus });
    if (result.unlocked && result.achievement) {
      unlockedAchievements.push(result.achievement);
    }
  }

  // Check Gold Standard (90%+ on 15 tests)
  if (scores90Plus >= 15) {
    const result = await unlockAchievement(userId, 'gold_standard', { count: scores90Plus });
    if (result.unlocked && result.achievement) {
      unlockedAchievements.push(result.achievement);
    }
  }

  // Check Diamond Elite (95%+ on 20 tests)
  if (scores95Plus >= 20) {
    const result = await unlockAchievement(userId, 'diamond_elite', { count: scores95Plus });
    if (result.unlocked && result.achievement) {
      unlockedAchievements.push(result.achievement);
    }
  }

  return unlockedAchievements;
}

// ============ LEADERBOARD MANAGEMENT ============

/**
 * Get top users for leaderboard (by total points)
 */
export async function getLeaderboard(period = 'alltime', limitCount = 50) {
  try {
    const q = query(
      collection(db, 'userProgress'),
      orderBy('totalPoints', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);

    const leaderboard = await Promise.all(
      snapshot.docs.map(async (progressDoc, index) => {
        const data = progressDoc.data();
        const level = getLevelByXP(data.totalXP || 0);
        const userId = progressDoc.id;

        // Prefer name/email saved in userProgress to avoid cross-user read restrictions
        let userName = data.userName || data.username || data.profileName || data.name || data.displayName || 'User';
        let userEmail = data.email || '';
        try {
          const userDocRef = doc(db, 'jobseekers', userId);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            userName = userData.fullName || userData.name || userData.displayName || 'User';
            userEmail = userData.email || '';
          } else {
            // Also try userProfiles collection (prefer fullName/displayName)
            const profileDocRef = doc(db, 'userProfiles', userId);
            const profileDocSnap = await getDoc(profileDocRef);
            if (profileDocSnap.exists()) {
              const profileData = profileDocSnap.data();
              const resumeInfo = profileData.resumeData?.personalInfo || {};
              userName = resumeInfo.fullName || resumeInfo.name || profileData.displayName || profileData.name || 'User';
              userEmail = resumeInfo.email || profileData.email || '';
            }
          }
        } catch (error) {
          console.error('Error fetching user details for', userId, ':', error);
        }

        const emailLocal = (userEmail || data.email || '').split('@')[0] || '';
        const safeName = data.isAnonymous === true
          ? `Anonymous #${userId.slice(0, 4)}`
          : (userName && userName !== 'User'
            ? userName
            : (userName || 'User'));

        return {
          userId,
          userName: safeName,
          userEmail: data.isAnonymous === true ? '' : userEmail,
          level: level.level,
          levelName: level.name,
          levelEmoji: level.emoji,
          points: data.totalPoints || 0,
          totalPoints: data.totalPoints || 0,
          currentStreak: data.currentStreak || 0,
          rank: index + 1,
          isAnonymous: data.isAnonymous === true, // Only anonymous if explicitly set to true
          avatar: data.avatar
        };
      })
    );

    return leaderboard;
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    return [];
  }
}

/**
 * Get user's rank on leaderboard
 */
export async function getUserRank(userId) {
  try {
    if (!userId) {
      return { rank: -1, totalUsers: 0 };
    }

    // Get all users ordered by points
    const q = query(
      collection(db, 'userProgress'),
      orderBy('totalPoints', 'desc')
    );

    const snapshot = await getDocs(q);
    const userIds = snapshot.docs.map(doc => doc.id);
    const rank = userIds.indexOf(userId) + 1;

    // Get user's points
    const userDoc = snapshot.docs.find(doc => doc.id === userId);
    const points = userDoc?.data()?.totalPoints || 0;

    console.log('User rank calculated:', { userId, rank, totalUsers: snapshot.size, points });

    return {
      rank: rank > 0 ? rank : -1,
      totalUsers: snapshot.size,
      points
    };
  } catch (error) {
    console.error('Error getting user rank:', error);
    return { rank: -1, totalUsers: 0 };
  }
}

// ============ ACTIVITY TRACKING ============

/**
 * Track user activity (call this on any significant action)
 */
export async function trackActivity(
  userId,
  activityType,
  userInfo = {}
) {
  // Update streak and persist identity if provided
  const streakResult = await updateStreak(userId, userInfo);

  let achievements = [];

  // Check for specific activity achievements
  if (activityType === 'resume_upload') {
    await addPoints(userId, POINTS.RESUME_UPLOADED);
    await addXP(userId, POINTS.RESUME_UPLOADED);
  } else if (activityType === 'profile_edit') {
    // Check if profile is 100% complete
    const progress = await getUserProgress(userId);
    // Add logic to check profile completeness
  } else if (activityType === 'project') {
    await addPoints(userId, POINTS.PROJECT_ADDED);
    await addXP(userId, POINTS.PROJECT_ADDED);
  }

  return {
    streakUpdated: streakResult.streakIncreased,
    achievementsUnlocked: achievements
  };
}

/**
 * MAINTENANCE: Backfill names for users who have missing/placeholder names
 * This ensures "old users" show up correctly in the leaderboard
 */
export async function fixLeaderboardNames() {
  console.log('Starting leaderboard name fix...');
  let updatedCount = 0;
  let skippedCount = 0;
  let errors = 0;

  try {
    // Get all user progress docs
    // optimizing to process in chunks could be better for large DBs, 
    // but assuming reasonable size for now
    const snapshot = await getDocs(collection(db, 'userProgress'));
    console.log(`Found ${snapshot.size} users to check.`);

    const updates = [];

    for (const docSnapshot of snapshot.docs) {
      const data = docSnapshot.data();
      const userId = docSnapshot.id;

      // Check if name is needs fixing
      const currentName = data.displayName || data.userName || data.username || data.name || data.profileName;
      const isPlaceholder = !currentName ||
        currentName === 'User' ||
        currentName.toLowerCase().startsWith('user ') ||
        currentName.includes('Anonymous');

      if (isPlaceholder) {
        // Find correct name
        let foundName = null;

        // Try jobseekers
        try {
          const jsDoc = await getDoc(doc(db, 'jobseekers', userId));
          if (jsDoc.exists()) {
            const jsData = jsDoc.data();
            foundName = jsData.fullName || jsData.name || jsData.displayName;
          }
        } catch (e) {
          // ignore
        }

        // Try userProfiles if still null
        if (!foundName) {
          try {
            const pDoc = await getDoc(doc(db, 'userProfiles', userId));
            if (pDoc.exists()) {
              const pData = pDoc.data();
              const info = pData.resumeData?.personalInfo || {};
              foundName = info.fullName || info.name || pData.displayName || pData.name;
            }
          } catch (e) {
            // ignore
          }
        }

        if (foundName && foundName !== 'User') {
          console.log(`Fixing name for ${userId}: ${foundName}`);
          // Update the doc
          updates.push(
            updateDoc(doc(db, 'userProgress', userId), {
              userName: foundName, // Standardize on userName
              displayName: foundName,
              updatedAt: serverTimestamp()
            })
          );
          updatedCount++;
        } else {
          console.log(`Could not find name for ${userId}`);
          skippedCount++;
        }
      } else {
        skippedCount++;
      }
    }

    // Execute all updates
    await Promise.all(updates);

    console.log(`Leaderboard fix complete. Updated: ${updatedCount}, Skipped: ${skippedCount}`);
    return { success: true, updatedCount, skippedCount };
  } catch (error) {
    console.error('Error fixing leaderboard names:', error);
    return { success: false, error };
  }
}

// ============ EXPORT MAIN FUNCTIONS ============

export const gamificationService = {
  // User Progress
  initializeUserProgress,
  getUserProgress,
  addXP,
  addPoints,

  // Streaks
  updateStreak,
  resetStreak,

  // Achievements
  hasAchievement,
  unlockAchievement,
  getUserAchievements,
  checkTestAchievements,
  checkPerformanceAchievements,

  // Leaderboard
  getLeaderboard,
  getUserRank,

  // Activity
  trackActivity,

  // Maintenance
  fixLeaderboardNames
};

export default gamificationService;

